import React, { useEffect, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { selectScreenSize, currentUser } from 'redux/session/selectors';
import { selectCurrentTeamId } from 'redux/team/selectors';
import { SCREEN_SIZE } from 'types';
import {
  DataTable,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableContainer,
  Pagination,
  Loading,
  TooltipIcon
} from 'carbon-components-react';
import { Help16 } from '@carbon/icons-react';
import Utils, {
  getDaysFromNow,
  toLocalDateTimeFormat,
  formFullAddress
} from 'utilities/helper.util';
import DisplayEnquiryRow from './rows';
import EmptyStatePage from './empty';
import { CFCProps } from 'types';
import { useEmptyCallback } from 'utilities/hooks.util';

import { useSaga } from 'use-reducer-saga';
import reducer, { initialState } from '../../localStore/reducer';
import sagas from '../../localStore/sagas';
import { getEnquiriesStart } from '../../localStore/actions';

const PageSizes = [10, 20, 50];
const fieldsToRetrieve = [
  'id',
  'address',
  'user_reference',
  'enquiry_at',
  'is_expired',
  'status',
  'shape',
  'geocoded_address',
  'projects',
  'users',
  'auto_renew',
  'auto_renew_every',
  'auto_renew_until',
  'auto_renew_date',
  'auto_renew_done',
  'auto_renew_enquiry_id',
  'internal_create',
  'external_id',
  'created_at',
  'is_limit_reached',
  'archived_at'
];

const getResponsesStatus = (assestOwner: any) => {
  let allResoponseReceived = true;
  if (assestOwner?.length > 0) {
    allResoponseReceived = assestOwner.find(
      (ao: any) => ao.status === 'waiting'
    )
      ? false
      : true;
  } else {
    allResoponseReceived = false;
  }
  return allResoponseReceived;
};

export const ListView: React.FC<Props> = ({
  handleSetEnquiryId,
  searchText,
  selectedRow,
  searchFilter,
  showMyEnquiriesOnly,
  currentPage,
  pageOffset,
  setCurrentPage,
  setPageOffset
}) => {
  const [localStore, localDispatch] = useSaga(reducer, initialState, sagas);
  const { enquiries: enquiriesList, isLoading: isLoadingEnquiries } =
    localStore;
  const screenSize = useSelector(selectScreenSize);
  const user = useSelector(currentUser);
  const currentTeamId = useSelector(selectCurrentTeamId);
  const [pageSize, setPageSize] = useState<number>(10);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [filteredEnquiryList, setFilteredEnquiryList] = useState<Array<any>>(
    []
  );

  const defaultStartDate = Utils.getNewDate()
    .subtract(30, 'day')
    .startOf('day')
    .utc()
    .format();
  const defaultEndDate = Utils.getNewDate().endOf('day').utc().format();

  const getListViewData = useEmptyCallback((queryParams: any) => {
    localDispatch(getEnquiriesStart(queryParams));
  });

  const handlePageChange = ({
    page,
    pageSize
  }: {
    page: number;
    pageSize: number;
  }) => {
    setPageOffset((page - 1) * pageSize);
    setCurrentPage(page);
    setPageSize(pageSize);
  };

  useEffect(() => {
    let userId = undefined;
    if (showMyEnquiriesOnly) {
      userId = user.id;
    } else if (searchFilter.enquirer && currentTeamId) {
      userId = searchFilter.enquirer;
    }
    const queryParams: any = {
      text: searchText || searchFilter.userReference,
      user: userId && userId !== '0' ? [userId] : undefined,
      project: currentTeamId ? [currentTeamId] : undefined,
      status: searchFilter.status,
      viewAll: true,
      range: searchFilter.range,
      fields: fieldsToRetrieve,
      limit: pageSize,
      offset: pageOffset,
      isLocateR: true
    };
    getListViewData(queryParams);
  }, [
    getListViewData,
    pageOffset,
    pageSize,
    defaultStartDate,
    defaultEndDate,
    currentTeamId,
    searchText,
    searchFilter,
    showMyEnquiriesOnly,
    user
  ]);

  useEffect(() => {
    if (enquiriesList) {
      setTotalItems(enquiriesList.count);
      const filteredList = enquiriesList.enquiries?.map((enquiry: any) => {
        const isExpired = enquiry.is_expired;
        const archivedAt = enquiry.archived_at;
        const statusOld = isExpired ? 'expired' : enquiry.status;
        const statusNew = isExpired
          ? 'expired'
          : archivedAt
          ? 'archived'
          : enquiry.status;
        const status =
          process.env.REACT_APP_SENTINEL_RESPONSE_COLLATION === 'true'
            ? statusNew
            : statusOld;

        const listObj = {
          id: enquiry.id?.toString(),
          enquirer: `${enquiry.Users?.first_name} ${enquiry.Users?.last_name}`,
          address: formFullAddress(enquiry.address),
          createdAt: toLocalDateTimeFormat(enquiry.enquiry_at),
          jobId: enquiry.external_id,
          status,
          statusTranslated: Utils.translateEnquiryStatus(
            status,
            archivedAt,
            false,
            true
          ),
          allResponsesReceived: getResponsesStatus(enquiry.Assetowner),
          userReference: enquiry.user_reference,
          autoRenew: enquiry.is_edit
            ? enquiry.auto_renew_edited
            : enquiry.auto_renew,
          autoRenewUntil: enquiry.is_edit
            ? enquiry.auto_renew_until_edited
            : enquiry.auto_renew_until,
          internalCreate: enquiry.internal_create,
          isExpired,
          isIgnore: enquiry.is_ignore,
          isLimitedReached: enquiry.is_limit_reached,
          isDataRotated:
            getDaysFromNow(enquiry.enquiry_at) >=
            Number(process.env.REACT_APP_SENTINEL_DATA_ROTATION_DAYS),
          archivedAt: archivedAt
        };
        return listObj;
      });
      setFilteredEnquiryList(filteredList);
    }
  }, [enquiriesList, isLoadingEnquiries]);

  const headers = [
    { key: 'jobId', header: 'Job', isSortable: true },
    { key: 'createdAt', header: '' },
    { key: 'enquirer', header: '' },
    { key: 'userReference', header: 'User reference', isSortable: true },
    { key: 'address', header: 'Address' },
    { key: 'status', header: 'Status' },
    { key: 'statusTranslated', header: '' }
  ];

  return (
    <div
      id="homeListView"
      className="home--list-view"
      data-testid="homeListView"
    >
      {!isLoadingEnquiries ? (
        <>
          {totalItems > 0 && filteredEnquiryList?.length !== 0 ? (
            <>
              <DataTable
                rows={filteredEnquiryList}
                headers={headers}
                isSortable={false}
              >
                {({ getTableProps, getRowProps }: any) => {
                  return (
                    <TableContainer>
                      <Table {...getTableProps()}>
                        {screenSize !== SCREEN_SIZE.SM && (
                          <TableHead>
                            <TableRow>
                              <TableHeader key="jobId">Job Id</TableHeader>
                              <TableHeader key="userReference">
                                User reference
                              </TableHeader>
                              <TableHeader key="address">Address</TableHeader>
                              <TableHeader key="status">
                                <div className="header-with-tooltip">
                                  <TooltipIcon
                                    tooltipText={'AR = Auto-renews'}
                                    align={'start'}
                                  >
                                    <Help16 />
                                  </TooltipIcon>
                                  &nbsp; Status
                                </div>
                              </TableHeader>
                            </TableRow>
                          </TableHead>
                        )}
                        <TableBody>
                          {filteredEnquiryList.map((row: any, index: any) => (
                            <DisplayEnquiryRow
                              row={row}
                              getRowProps={getRowProps}
                              key={index}
                              idx={index}
                              handleSetEnquiryId={handleSetEnquiryId}
                              smallscreen={
                                screenSize === SCREEN_SIZE.SM
                                  ? 'true'
                                  : undefined
                              }
                              selectedRow={selectedRow}
                            />
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  );
                }}
              </DataTable>
              <Pagination
                onChange={(data) => handlePageChange(data)}
                backwardText="Previous page"
                forwardText="Next page"
                itemsPerPageText="Display:"
                page={currentPage}
                pageNumberText="Page Number"
                pageSize={pageSize}
                pageSizes={PageSizes}
                totalItems={totalItems}
              />
            </>
          ) : (
            <EmptyStatePage />
          )}
        </>
      ) : (
        <div className="loader--wrapper">
          <Loading description="Active loading indicator" withOverlay={false} />
        </div>
      )}
    </div>
  );
};

ListView.displayName = 'ListView';

const connector = connect();

type OwnProps = {
  handleSetEnquiryId: Function;
  isListView: boolean;
  searchText: string;
  selectedRow: any;
  searchFilter: any;
  showMyEnquiriesOnly: boolean;
  currentPage: number;
  setCurrentPage: Function;
  pageOffset: number;
  setPageOffset: Function;
};

type Props = CFCProps<typeof connector> & OwnProps;

export default React.memo(connector(ListView));
